import useSupaSocket from '@/hooks/useSupaSocket';
import { PulseReport, featureService } from '@common/api/featureService';
import dayjs from 'dayjs';
import {
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react';

export type TimeFilter = 'All' | 'Today' | 'This Week' | 'This Month';

export interface PulseState {
    pulseData: PulseReport[];
    loading: boolean;
    activeFilter: TimeFilter;
    setPulseData: (data: PulseReport[]) => void;
    setLoading: (loading: boolean) => void;
    setActiveFilter: (filter: TimeFilter) => void;
    fetchPulseReports: (filter?: TimeFilter) => Promise<void>;
    clearPulseData: () => void;
}

export const pulseInitialState: PulseState = {
    pulseData: [],
    loading: false,
    activeFilter: 'Today',
    setPulseData: () => {},
    setLoading: () => {},
    setActiveFilter: () => {},
    fetchPulseReports: async () => {},
    clearPulseData: () => {}
};

// Helper function to get date range for time filters
const getDateRange = (filter: TimeFilter): { start_time?: string; end_time?: string } => {
    const today = dayjs().startOf('day');
    
    switch (filter) {
        case 'Today':
            return {
                start_time: today.toISOString(),
                end_time: today.add(1, 'day').toISOString()
            };
        case 'This Week':
            const weekStart = today.startOf('week');
            return {
                start_time: weekStart.toISOString(),
                end_time: today.add(1, 'day').toISOString()
            };
        case 'This Month':
            const monthStart = today.startOf('month');
            return {
                start_time: monthStart.toISOString(),
                end_time: today.add(1, 'day').toISOString()
            };
        case 'All':
        default:
            return {};
    }
};

export const createPulseProvider = (): PulseState => {
    const [pulseData, setPulseData] = useState<PulseReport[]>([]);
    const [loading, setLoading] = useState(false);
    const [activeFilter, setActiveFilter] = useState<TimeFilter>('Today');


    const fetchPulseReports = useCallback(async (filter?: TimeFilter, refresh: boolean = true) => {
        const targetFilter = filter || activeFilter;
        try {
            if (refresh) {
                setLoading(true);
            }
            
            const dateRange = getDateRange(targetFilter);
            const response = await featureService.getPulseReports({
                ...dateRange,
                size: 999,
                page: 1
            });
            
            setPulseData(response.data || []);
        } catch (err) {
            console.error('Failed to fetch pulse reports:', err);
            setPulseData([]);
        } finally {
            setLoading(false);
        }
    }, [activeFilter]);

    // 监听 activeFilter 变化，自动获取数据
    useEffect(() => {
        fetchPulseReports(activeFilter);
    }, [activeFilter, fetchPulseReports]);

    const clearPulseData = useCallback(() => {
        setPulseData([]);
    }, []);

    const callbacks = useMemo(()=>{
        return {
            onChange: (payload: any) => {
                fetchPulseReports(activeFilter, false);
            }
        }
    }, [activeFilter, fetchPulseReports]);
    
    useSupaSocket({
        table: 'user_pulse_report',
        enabled: true
    }, callbacks);
    
    return {
        pulseData,
        loading,
        activeFilter,
        setPulseData,
        setLoading,
        setActiveFilter,
        fetchPulseReports,
        clearPulseData
    };
};